import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAuthSnapshot } from './useAuthSnapshot';
import { LoginCredentials, SignupData } from '../types/auth';
import { api } from '../services/api';
import { User } from '../types/user';
import { anonymousTestHandler } from '../services/anonymousTestHandler';

export function useAuth() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hydration-safe store access — see useAuthSnapshot. Actions are stable
  // references, so reading them once via getState() is equivalent to
  // destructuring from the reactive hook.
  const { user, isAuthenticated } = useAuthSnapshot();
  const { login: storeLogin, logout: storeLogout } = useAuthStore.getState();

  const handleNavigation = useCallback((user: User) => {
    if (user.role === 'admin') {
      navigate('/admin');
    } else if (user.role === 'expert') {
      navigate('/expert/dashboard');
    } else if (user.hasCompletedTest || (user.enneagramType !== undefined && user.enneagramType !== null)) {
      navigate('/dashboard');
    } else {
      navigate('/test');
    }
  }, [navigate]);

  // Check and attempt to claim anonymous test results
  const checkAndClaimAnonymousResults = useCallback(async (userId: string) => {
    if (anonymousTestHandler.hasStoredResults()) {
      try {
        return await anonymousTestHandler.claimTestResults(userId);
      } catch (error) {
        console.error('Error claiming anonymous test results:', error);
      }
    }
    return false;
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.auth.login(credentials);
      const user: User = { ...response.user };
      if (!user.role) user.role = 'user';
      if (user.role === 'admin') user.hasCompletedTest = true;

      storeLogin(user, response.token);

      const claimed = await checkAndClaimAnonymousResults(user.id);
      if (claimed) {
        try {
          const updatedUser = await api.auth.getCurrentUser();
          if (updatedUser) storeLogin({ ...updatedUser, id: user.id }, response.token);
        } catch (refreshError) {
          console.error('Error refreshing user after claiming test:', refreshError);
        }
      }

      setTimeout(() => handleNavigation(user), 100);
    } catch (error) {
      console.error('Login error:', error);
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, storeLogin, handleNavigation, checkAndClaimAnonymousResults]);

  const signup = useCallback(async (credentials: SignupData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.auth.signup(credentials);
      const user: User = { ...response.user };
      storeLogin(user, response.token);
      
      // Check for anonymous test results and claim them if found
      await checkAndClaimAnonymousResults(user.id);
      
      handleNavigation(user);
    } catch (error) {
      console.error('Signup error:', error);
      setError('Error creating account');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, storeLogin, handleNavigation, checkAndClaimAnonymousResults]);

  const logout = useCallback(async () => {
    await storeLogout();
    navigate('/login');
  }, [navigate, storeLogout]);

  return {
    login,
    signup,
    logout,
    isLoading,
    error,
    user,
    isAuthenticated
  };
}